import * as React from 'react';
import Box from '@mui/material/Box';
import { IconButton, Typography } from '@mui/material';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import PropTypes from 'prop-types';
import SettingsIcon from '@mui/icons-material/Settings';
import { WAREHOUSE_HEIGHT, WAREHOUSE_ICO_COLOR } from './constants';
import DetailsDialog from './DetailsDialog';
import ElevatorStatuses from './components/ElevatorStatuses';
import VehicleStatuses from './components/VehicleStatuses';
import SmallText from './ui/WarehouseItem/SmallText';
import ElevatorChart from './components/ElevatorChartPopup/ElevatorChartPopup';
import StartStopWarehouse from './components/StartStopWarehouse/StartStopWarehouse';
import WarehouseActiveRegNums from './components/WarehouseActiveRegNums/WarehouseActiveRegNums';

export default function WarehouseItem(props) {
  const {
    warehouse: { name, geozoneUnloadId, solutionRoutes, dayCropCapacity, hourCropCapacity },
    bgColor,
    warehouse,
    chartData,
    planVolume,
    vehicles,
    ...other
  } = props;
  const [isOpenChart, setIsOpenChart] = React.useState(false);

  const warehouseVehiclesIds = React.useMemo(
    () => [...new Set(solutionRoutes?.map((e) => e?.vehicleId) || [])],
    [solutionRoutes],
  );

  const [warehouseItemDetails, setWarehouseItemDetails] = React.useState(null);

  const currentChartData = chartData
    .filter((data) => data.consumerId === warehouse.geozoneUnloadId)
    .sort((a, b) => new Date(a.x) - new Date(b.x));

  const openWarehouseDetailsDialog = () => {
    setWarehouseItemDetails(warehouse);
  };

  const closeWarehouseDetailsDialog = () => {
    setWarehouseItemDetails(null);
  };

  const handleOpenChart = () => setIsOpenChart(true);
  const handleCloseChart = () => setIsOpenChart(false);

  return (
    <Box
      sx={{
        borderRadius: '4px',
        height: `${WAREHOUSE_HEIGHT}px`,
        display: 'flex',
        background: bgColor,
        gap: '12px',
        alignItems: 'center',
        overflow: 'hidden',
      }}
      {...other}
      pl="12px"
    >
      <Box
        sx={{
          lineHeight: 1,
          minWidth: '30%',
          flexGrow: 1,
          color: WAREHOUSE_ICO_COLOR,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: 'flex',
          gap: '10px',
          alignItems: 'center',
        }}
      >
        <SmallText>
          {name}
          <br />
          {dayCropCapacity ? `${dayCropCapacity} т/д` : ''}
          <br />
          {hourCropCapacity ? `${hourCropCapacity} т/ч` : ''}
        </SmallText>
        <ElevatorStatuses id={geozoneUnloadId} />
        <WarehouseActiveRegNums vehicleIds={warehouseVehiclesIds} />
      </Box>
      <VehicleStatuses ids={warehouseVehiclesIds} />
      <Box
        sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '2px', mr: '8px' }}
      >
        <StartStopWarehouse warehouseId={warehouse.geozoneUnloadId} status={warehouse.status} />
        <IconButton
          sx={{ margin: '0 3px' }}
          title="Информация о складе"
          id="basic-button"
          aria-controls={warehouseItemDetails ? 'basic-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={warehouseItemDetails ? 'true' : undefined}
          onClick={openWarehouseDetailsDialog}
        >
          <SettingsIcon sx={{ color: WAREHOUSE_ICO_COLOR }} />
        </IconButton>
        <IconButton title="График готовой продукции" onClick={handleOpenChart}>
          <InsertChartOutlinedIcon sx={{ color: WAREHOUSE_ICO_COLOR }} />
        </IconButton>
        <Typography fontSize="small" sx={{ color: WAREHOUSE_ICO_COLOR, minWidth: '3rem', textAlign: 'center' }}>
          {`${Number(currentChartData?.[currentChartData.length - 1]?.y.toFixed(2)) || 0} т`}
        </Typography>
        <DetailsDialog type="warehouse" item={warehouseItemDetails} closeItemDialog={closeWarehouseDetailsDialog} />
      </Box>

      <ElevatorChart
        name={warehouse?.name}
        data={currentChartData}
        planVolume={planVolume}
        vehicles={vehicles}
        open={isOpenChart}
        onClose={handleCloseChart}
      />
    </Box>
  );
}

WarehouseItem.propTypes = {
  bgColor: PropTypes.string,
  warehouse: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    geozoneUnloadId: PropTypes.number.isRequired,
    solutionRoutes: PropTypes.arrayOf(
      PropTypes.shape({
        vehicleId: PropTypes.number.isRequired,
      }),
    ),
    dayCropCapacity: PropTypes.number.isRequired,
    hourCropCapacity: PropTypes.number.isRequired,
    status: PropTypes.string.isRequired,
  }),
  chartData: PropTypes.arrayOf(PropTypes.shape()),
  planVolume: PropTypes.number.isRequired,
  vehicles: PropTypes.arrayOf(PropTypes.shape()),
};

WarehouseItem.defaultProps = {
  bgColor: '#ebe4a7',
  warehouse: {
    geozoneUnloadId: null,
    solutionRoutes: [],
  },
  chartData: [],
  vehicles: [],
};
